import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Calculator, Copy, Check, ArrowUpCircle, Tag, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useSite } from '../context/SiteContext';
import { addSaving } from '../utils/savings';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Parse coupon title and compute the savings this coupon gives on a given amount.
// Returns { savings, isPercent, rawValue }
const computeSavings = (coupon, amount) => {
  const title = String(coupon.title || '');
  // Match number and optional % sign. Accepts "$50", "-33%", "50%", "$1,999"
  const m = title.replace(/,/g, '').match(/(\d+(?:\.\d+)?)\s*(%?)/);
  if (!m) return 0;
  const num = parseFloat(m[1]);
  const isPercent = m[2] === '%';
  const minPurchase = coupon.min_purchase || 0;
  if (amount < minPurchase) return 0;
  let savings = isPercent ? (amount * num) / 100 : num;
  if (coupon.max_discount) savings = Math.min(savings, coupon.max_discount);
  return Math.round(savings);
};

const fmt = (n) => `$${Number(n || 0).toLocaleString('es-MX')}`;

const CouponFinder = ({ resetSignal = 0 }) => {
  const { settings, getImageUrl, darkMode } = useSite();
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [best, setBest] = useState(null);      // Best applicable coupon
  const [nextUp, setNextUp] = useState(null);  // Coupon just out of reach (needs more $ to unlock)
  const [hasBankCoupons, setHasBankCoupons] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupCoupon, setPopupCoupon] = useState(null);
  const [countdown, setCountdown] = useState(5);
  // Ref to the pending navigation setTimeout so we can cancel it if the user
  // returns via BFCache before it fires.
  const navTimerRef = useRef(null);

  // Reset all state when parent triggers a refresh
  useEffect(() => {
    if (resetSignal === 0) return;
    setAmount('');
    setBest(null);
    setNextUp(null);
    setHasBankCoupons(false);
    setShowResult(false);
    setCopied(false);
    setShowPopup(false);
    setPopupCoupon(null);
  }, [resetSignal]);

  useEffect(() => {
    if (showPopup && countdown > 0) {
      const t = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(t);
    } else if (showPopup && countdown === 0 && popupCoupon) {
      const link = popupCoupon.affiliate_link || settings.affiliate_link || 'https://mercadolibre.com';
      setShowPopup(false);
      navTimerRef.current = setTimeout(() => { window.location.href = link; }, 30);
    }
  }, [showPopup, countdown, popupCoupon, settings.affiliate_link]);

  // Reset popup state on any return-to-page event
  useEffect(() => {
    const resetPopup = () => {
      // Cancel any pending navigation timer left over from BFCache
      if (navTimerRef.current) {
        clearTimeout(navTimerRef.current);
        navTimerRef.current = null;
      }
      setShowPopup(false);
      setCountdown(settings.button_redirect_seconds || 5);
      setCopied(false);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') resetPopup();
    };
    window.addEventListener('pageshow', resetPopup);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', resetPopup);
    return () => {
      window.removeEventListener('pageshow', resetPopup);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', resetPopup);
    };
  }, [settings.button_redirect_seconds]);

  const goNow = () => {
    if (!popupCoupon) return;
    const link = popupCoupon.affiliate_link || settings.affiliate_link || 'https://mercadolibre.com';
    setShowPopup(false);
    navTimerRef.current = setTimeout(() => { window.location.href = link; }, 30);
  };

  if (settings.finder_enabled === false) return null;

  const handleFind = async () => {
    const value = parseFloat(String(amount).replace(/[^\d.]/g, ''));
    if (!value || value <= 0) {
      toast.error('Ingresa un monto válido');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/coupons`);
      const list = (res.data || []).filter(c => {
        const kind = c.item_kind || 'coupon';
        // Only real coupons — exclude "product" offers
        if (kind !== 'coupon') return false;
        // Exclude expired
        if (c.expiration_date && new Date(c.expiration_date) < new Date()) return false;
        // Exclude sold out (agotado): stock defined and depleted
        if (c.stock !== null && c.stock !== undefined && c.stock <= 0) return false;
        return true;
      });

      // Applicable coupons: only STORE type
      const applicable = list
        .filter(c => (c.coupon_type || 'store') === 'store')
        .map(c => ({ c, savings: computeSavings(c, value) }))
        .filter(x => x.savings > 0)
        .sort((a, b) => b.savings - a.savings);

      // Not-yet-applicable: any store coupon with higher min_purchase (closest tier first)
      const almost = list
        .filter(c => (c.coupon_type || 'store') === 'store')
        .filter(c => (c.min_purchase || 0) > value)
        .map(c => ({ c, savings: computeSavings(c, c.min_purchase || 0), gap: (c.min_purchase || 0) - value }))
        .filter(x => x.savings > 0)
        .sort((a, b) => a.gap - b.gap);

      // Check if there are any active bank coupons to suggest combining
      const hasBank = list.some(c => (c.coupon_type || '') === 'bank');
      setHasBankCoupons(hasBank);

      setBest(applicable[0] || null);
      setNextUp(almost[0] || null);
      setShowResult(true);
      setCopied(false);
    } catch (err) {
      toast.error('Error al buscar cupones');
    } finally {
      setLoading(false);
    }
  };

  // Synchronous, non-blocking clipboard copy (mobile deep-link safe, see CouponCard)
  const copyToClipboard = (text) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text || '';
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch (e) {
      console.error('Clipboard fallback failed:', e);
    }
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text || '').catch(() => {});
    }
  };

  const handleCopyAndRedeem = (coupon) => {
    // Update UI state FIRST (mobile deep-link fix: clipboard can hang without focus)
    setPopupCoupon(coupon);
    setCountdown(settings.button_redirect_seconds || 5);
    setShowPopup(true);
    setCopied(true);

    copyToClipboard(coupon.code);

    axios.post(`${API}/coupons/${coupon.id}/click`).catch(() => {});
    // Mark coupon as used (same as CouponCard)
    try {
      const used = JSON.parse(localStorage.getItem('usedCoupons') || '{}');
      used[coupon.id] = { usedAt: new Date().toISOString(), code: coupon.code };
      localStorage.setItem('usedCoupons', JSON.stringify(used));
    } catch (e) {
      console.error('usedCoupons write failed:', e);
    }
    // Track savings — use the actual computed savings for this purchase amount
    const purchaseVal = parseFloat(String(amount).replace(/[^\d.]/g, '')) || 0;
    const bestForCoupon = best?.c?.id === coupon.id ? best.savings : null;
    addSaving(coupon, bestForCoupon != null ? { amountOverride: bestForCoupon } : { amountRef: purchaseVal });
  };

  const purchaseValue = parseFloat(String(amount).replace(/[^\d.]/g, '')) || 0;
  const borderColor = settings.finder_border_color || '#10b981';
  const badgeBg = settings.finder_best_badge_bg || '#10b981';
  const badgeColor = settings.finder_best_badge_text_color || '#ffffff';
  const savingsColor = settings.finder_savings_text_color || '#2563eb';
  const copyBg = settings.finder_copy_button_bg || '#fbbf24';
  const copyText = settings.finder_copy_button_text_color || '#000000';
  const hintBg = settings.finder_hint_bg || '#EFF6FF';
  const hintText = settings.finder_hint_text_color || '#1e293b';
  const hintAccent = settings.finder_hint_accent_color || '#2563eb';
  const buttonBg = settings.finder_button_bg || '#3B82F6';
  const buttonText = settings.finder_button_text_color || '#ffffff';

  // Render a template with {gap}/{savings}/{min} tokens replaced by styled spans
  const renderHintTemplate = (template, values) => {
    if (!template) return null;
    const parts = String(template).split(/(\{gap\}|\{savings\}|\{min\})/g);
    return parts.map((part, i) => {
      if (part === '{gap}') return <span key={i} className="font-bold" style={{ color: hintAccent }}>{values.gap}</span>;
      if (part === '{savings}') return <span key={i} className="font-bold" style={{ color: hintAccent }}>{values.savings}</span>;
      if (part === '{min}') return <span key={i} className="font-bold" style={{ color: hintAccent }}>{values.min}</span>;
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <div className="mb-3 sm:mb-6" data-testid="coupon-finder">
      {/* Input row */}
      <div className={`flex items-center gap-2 rounded-xl border-2 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-neutral-200'} p-1.5 sm:p-2`}>
        <div className={`flex items-center flex-1 gap-2 pl-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
          {settings.finder_emoji ? (
            <span className="text-xl leading-none flex-shrink-0" data-testid="finder-emoji">{settings.finder_emoji}</span>
          ) : (
            <Calculator className="w-5 h-5 flex-shrink-0" />
          )}
          <input
            type="number"
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleFind(); }}
            placeholder={settings.finder_placeholder || '¿Cuánto cuesta tu compra?'}
            className={`flex-1 outline-none bg-transparent text-sm sm:text-base font-medium py-2 ${darkMode ? 'placeholder:text-slate-500' : 'placeholder:text-slate-400'}`}
            data-testid="finder-amount-input"
          />
        </div>
        <button
          onClick={handleFind}
          disabled={loading}
          data-testid="finder-submit-button"
          className="px-4 sm:px-6 py-2.5 rounded-lg font-bold text-sm sm:text-base transition-all active:scale-[0.98] hover:opacity-90"
          style={{ backgroundColor: buttonBg, color: buttonText }}
        >
          {loading ? '...' : (settings.finder_button_text || 'Encontrar')}
        </button>
      </div>

      {/* Result card */}
      {showResult && (
        <div
          className={`mt-3 rounded-2xl overflow-hidden ${darkMode ? 'bg-slate-800' : 'bg-white'}`}
          style={{ border: `2px solid ${borderColor}` }}
          data-testid="finder-result-card"
        >
          {best ? (
            <>
              <div className="p-3 sm:p-4 flex items-center gap-3">
                {settings.finder_image_url && (
                  <img
                    src={getImageUrl(settings.finder_image_url)}
                    alt=""
                    width={112}
                    height={112}
                    loading="lazy"
                    decoding="async"
                    className="w-20 h-20 sm:w-28 sm:h-28 object-contain flex-shrink-0"
                    data-testid="finder-image"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <span
                    className="inline-block px-3 py-1 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-wide mb-1.5"
                    style={{ backgroundColor: badgeBg, color: badgeColor }}
                  >
                    {settings.finder_best_badge_text || 'MEJOR OPCIÓN'}
                  </span>
                  <p className="font-fredoka font-black text-2xl sm:text-3xl leading-none" style={{ color: savingsColor }}>
                    {fmt(best.savings)} <span className={`text-sm sm:text-base font-bold ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>{settings.finder_savings_suffix || 'de ahorro'}</span>
                  </p>
                  <div className={`mt-2 text-[11px] sm:text-sm font-medium space-y-0.5 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    {best.c.min_purchase && (
                      <div className="flex items-center gap-1.5">
                        <Tag className="w-3.5 h-3.5" />
                        <span>Mín. {fmt(best.c.min_purchase)}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5">
                      <Calculator className="w-3.5 h-3.5" />
                      <span>Tu total: {fmt(purchaseValue - best.savings)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-3 pb-3 sm:px-4 sm:pb-4">
                <button
                  onClick={() => handleCopyAndRedeem(best.c)}
                  data-testid="finder-copy-button"
                  className="w-full py-2.5 sm:py-3 rounded-xl font-bold text-sm sm:text-base transition-all active:scale-[0.98] hover:opacity-90 flex items-center justify-center gap-2"
                  style={{ backgroundColor: copyBg, color: copyText }}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  <span>{copied ? '¡Copiado!' : (settings.finder_copy_button_text || 'Copiar y canjear')}</span>
                </button>
              </div>

              {nextUp && (
                <div
                  className="mx-3 sm:mx-4 mb-3 sm:mb-4 rounded-xl p-3 flex items-start gap-2.5"
                  style={{ backgroundColor: hintBg, color: hintText }}
                  data-testid="finder-hint"
                >
                  <ArrowUpCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: hintAccent }} />
                  <div className="text-[11px] sm:text-sm leading-snug">
                    <p>
                      {renderHintTemplate(
                        settings.finder_hint_gap_text || 'Solo agrega {gap} más para el cupón de compra mínima de {min}',
                        { gap: fmt(nextUp.gap), savings: fmt(nextUp.savings), min: fmt(nextUp.c.min_purchase || 0) }
                      )}
                    </p>
                  </div>
                </div>
              )}
              {hasBankCoupons && (
                <div
                  className="mx-3 sm:mx-4 mb-3 sm:mb-4 rounded-xl p-2.5 text-[11px] sm:text-sm font-bold text-center"
                  style={{ backgroundColor: settings.finder_bank_combo_bg || '#FEF3C7', color: settings.finder_bank_combo_text_color || '#78350f' }}
                  data-testid="finder-bank-combo"
                >
                  {settings.finder_bank_combo_text || '💳 Combínalo con un cupón bancario si tienes disponible'}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="p-4 flex items-center gap-3">
                {settings.finder_image_url && (
                  <img src={getImageUrl(settings.finder_image_url)} alt="" className="w-16 h-16 sm:w-20 sm:h-20 object-contain flex-shrink-0 opacity-90" />
                )}
                <p className={`text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  {settings.finder_no_result_text || 'No encontramos un cupón para ese monto. Prueba con un monto mayor.'}
                </p>
              </div>
              {nextUp && (
                <div
                  className="mx-3 sm:mx-4 mb-3 sm:mb-4 rounded-xl p-3 flex items-start gap-2.5"
                  style={{ backgroundColor: hintBg, color: hintText }}
                  data-testid="finder-hint"
                >
                  <ArrowUpCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: hintAccent }} />
                  <div className="text-[11px] sm:text-sm leading-snug">
                    <p>
                      {renderHintTemplate(
                        settings.finder_hint_gap_text || 'Solo agrega {gap} más para el cupón de compra mínima de {min}',
                        { gap: fmt(nextUp.gap), savings: fmt(nextUp.savings), min: fmt(nextUp.c.min_purchase || 0) }
                      )}
                    </p>
                  </div>
                </div>
              )}
              {hasBankCoupons && (
                <div
                  className="mx-3 sm:mx-4 mb-3 sm:mb-4 rounded-xl p-2.5 text-[11px] sm:text-sm font-bold text-center"
                  style={{ backgroundColor: settings.finder_bank_combo_bg || '#FEF3C7', color: settings.finder_bank_combo_text_color || '#78350f' }}
                >
                  {settings.finder_bank_combo_text || '💳 Combínalo con un cupón bancario si tienes disponible'}
                </div>
              )}
            </>
          )}
        </div>
      )}
      {/* Popup: same style as CouponCard */}
      {showPopup && popupCoupon && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4 pb-24 sm:pb-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.25)' }}
          onClick={(e) => e.target === e.currentTarget && goNow()}
          data-testid="finder-popup"
        >
          <div className="bg-slate-800 text-white rounded-2xl p-5 shadow-2xl max-w-sm w-full">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                <Check className="w-5 h-5 text-white" strokeWidth={3} />
              </div>
              <span className="font-mono font-bold text-2xl tracking-wider text-canary">
                {popupCoupon.code}
              </span>
            </div>
            <p className="text-base text-slate-300 text-center mb-4">
              {settings.button_redirect_message || 'Pégalo en la sección de cupones de Mercado Libre'}
            </p>
            <button
              onClick={goNow}
              data-testid="finder-popup-go"
              className="w-full py-3 px-4 bg-canary text-slate-900 font-bold rounded-xl flex items-center justify-center gap-2 hover:bg-yellow-400 transition-colors active:scale-[0.98]"
            >
              <ExternalLink className="w-5 h-5" />
              Ir a Mercado Libre
              <span className="bg-slate-800 text-canary text-sm px-2.5 py-1 rounded-full font-bold ml-2">
                {countdown}s
              </span>
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CouponFinder;
